import {
	CommandDialogWithPreviewCard,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@lab/src/components/ui/command";
import { useTemplatesStore } from "@/dentlab/src/store/Templates";
import { displayTemplateLabel } from "@/dentlab/src/store/Templates/utils";
import { JobItemsTemplatePreview } from "@/dentlab/src/components/template-preview/JobItemsTemplatePreview";
import { DefaultTechnicianSelect } from "@/dentlab/src/pages/job-page/job-document/job-document-components/job-items-table/job-items-table-components/default-technician-select.component";
import { useRef, useState, useEffect } from "react";
import { RoundToType } from "@/dentlab/src/store/Jobs/utils";

interface TemplateSelectDialogProps {
	open: boolean;
	setOpen: (open: boolean) => void;
	onSelect: (templateId: number) => void;
	disabled?: boolean;
	priceListId?: string | null;
	tpValue?: number;
	taxRate?: number;
	roundTo?: RoundToType;
}

export const TemplateSelectDialog: React.FC<TemplateSelectDialogProps> = ({
	open,
	setOpen,
	onSelect,
	disabled = false,
	priceListId = null,
	tpValue = 1,
	taxRate = 8.1,
	roundTo = RoundToType.TO_RAPPEN,
}) => {
	const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(
		null
	);
	const [searchValue, setSearchValue] = useState("");
	const listRef = useRef<HTMLDivElement>(null);
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);

	const jobDocumentTemplateOptions = useTemplatesStore((state) =>
		Object.values(state.templatesLookup).map((template) => ({
			label: displayTemplateLabel(template.code, template.name),
			id: template.id,
		}))
	);

	const { templatesLookup } = useTemplatesStore((state) => ({
		templatesLookup: state.templatesLookup,
	}));

	useEffect(() => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}

		timeoutRef.current = setTimeout(() => {
			if (listRef.current) {
				listRef.current.scrollTo({ top: 0 });
			}
		}, 0);

		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, [searchValue]);

	return (
		<CommandDialogWithPreviewCard
			onOpenChange={setOpen}
			open={open}
			previewCard={
				selectedTemplateId ? (
					<div className="w-[400px] bg-white rounded-md p-2">
						<JobItemsTemplatePreview
							templateItems={
								selectedTemplateId
									? templatesLookup[selectedTemplateId]
											?.template_items ?? []
									: []
							}
							showTotals={true}
							priceListId={priceListId}
							tpValue={tpValue}
							taxRate={taxRate}
							roundTo={roundTo}
						/>
					</div>
				) : null
			}
		>
			<CommandInput
				placeholder="Auftragsvorlage suchen…"
				className="px-4"
				onValueChange={setSearchValue}
			/>{" "}
			<CommandList ref={listRef} className="w-[640px] h-96 max-h-96">
				<CommandGroup heading="Jumbos/Vorlagen" className="w-full">
					{jobDocumentTemplateOptions.map((template) => {
						return (
							<CommandItem
								onSelect={() => {
									onSelect(template.id);
									setOpen(false);
								}}
								key={template.id}
								// The options are unique because the codes are unique
								value={template.label ?? template.id.toString()}
								disabled={disabled}
								onMouseEnter={() => {
									setSelectedTemplateId(template.id);
								}}
								onMouseLeave={() => {
									setSelectedTemplateId(null);
								}}
							>
								<span>{template.label}</span>
							</CommandItem>
						);
					})}
				</CommandGroup>
			</CommandList>
			<div
				className="flex items-center justify-between border-t px-4 py-2"
				onKeyDown={(e) => e.stopPropagation()}
			>
				<span className="text-xs text-muted-foreground">
					Techniker für neue Positionen
				</span>
				<DefaultTechnicianSelect />
			</div>
		</CommandDialogWithPreviewCard>
	);
};
